import { localizationService, useLocalization } from '@capsplus/mfe-common';
import { withAuthGuard } from '@capsplus/shared-auth';
import React, { useCallback, useEffect, useState } from 'react';
import { Route, Routes, useNavigate } from 'react-router-dom';

import './App.css';

import Home from './components/Home';
import { iconsRegistry } from './components/icons/IconsRegistry';
import Footer from './components/layout/Footer';
import Header from './components/layout/Header';
import LeftMenu from './components/layout/LeftMenu';
import TabPanel from './components/layout/TabPanel';
import LoadingScreen from './components/loading/LoadingScreen';
import MFEComponentWrapper from './components/mfe/MFEComponentWrapper';
import { componentRegistry } from './components/navigation/ComponentRegistry';
import './components/pages/FormsFields.css';
import './components/pages/ModalsCommon.css';
import ToastContainer from './components/Toast/ToastContainer';
import { NotificationProvider } from './contexts/NotificationContext';
import { useLoadDataUser } from './hooks/useLoadDataUser';
import ProtectedRoute from './login/ProtectedRoute';
import permissionService from './services/permissionCheckService';
import { getMFEMetadata, shouldWrapWithErrorBoundary } from './utils/mfeHelpers';

const Notifications = React.lazy(() =>
  import('./components/pages/notifications/Notifications')
);

const DashboardIcon = iconsRegistry['Dashboard'];
const NotificationIcon = iconsRegistry['Notifications'] || (() => '🔔');

function App({ authState }) {
  const { L } = useLocalization();
  const navigate = useNavigate();
  const {
    isLoadingUserData,
    activeUserSession,
    menuItems,
    activeUser,
    userDataLoaded,
  } = useLoadDataUser(authState);
  const [isMenuCollapsed, setIsMenuCollapsed] = useState(false);

  const [tabs, setTabs] = useState([]);
  const [activeTab, setActiveTab] = useState('home');
  const [localizationReady, setLocalizationReady] = useState(false);
  const [mfeRetryKeys, setMfeRetryKeys] = useState({});

  useEffect(() => {
    // Initialize localization on app start
    const initLocalization = async () => {
      try {
        console.log('🔄 Initializing localization...');
        // Configure localization service with auth service getter
        const { getLocalization } = await import('./services/authService');
        const success = await localizationService.initialize(getLocalization);
        setLocalizationReady(true);

        if (success) {
          console.log('✅ Localization ready');
        } else {
          console.warn('⚠️ Localization initialized with fallback');
        }
      } catch (error) {
        console.error('❌ Localization initialization failed:', error);
        setLocalizationReady(true); // Continue anyway with fallback
      }
    };

    initLocalization();
  }, []);

  useEffect(() => {
    // Initialize home tab once localization is ready
    if (localizationReady && tabs.length === 0) {
      setTabs([
        {
          id: 'home',
          title: L('Dashboard'),
          icon: (
            <span>
              <DashboardIcon />
            </span>
          ),
          content: <Home />,
          closable: false,
        },
      ]);
    }
  }, [localizationReady, tabs.length, L]);

  useEffect(() => {
    // Initialize permission service on app start
    const initPermissions = async () => {
      if (authState?.isAuthenticated && activeUserSession) {
        try {
          console.log('🔄 Initializing permissions...');
          const success = await permissionService.initialize();

          if (success) {
            console.log('✅ Permissions ready');
          } else {
            console.warn('⚠️ Permissions initialized with fallback');
          }
        } catch (error) {
          console.error('❌ Permissions initialization failed:', error);
        }
      }
    };

    initPermissions();
  }, [authState?.isAuthenticated, activeUserSession]);
  // Handler to open notifications as a tab
  const handleOpenNotificationsTab = useCallback(() => {
    const notificationTabId = 'notifications';

    // Check if notifications tab already exists
    const existingTab = tabs.find((tab) => tab.id === notificationTabId);

    if (existingTab) {
      // If exists, just activate it
      setActiveTab(notificationTabId);
    } else {
      // Create new notifications tab
      const newTab = {
        id: notificationTabId,
        title: L('Notifications') || 'Notifications',
        icon: (
          <span>
            <NotificationIcon />
          </span>
        ),
        content: <Notifications isInTab={true} />,
        closable: true,
      };

      setTabs((prev) => [...prev, newTab]);
      setActiveTab(notificationTabId);
    }
  }, [tabs, L]);

  // Handler functions
  const handleMenuClick = useCallback(
    (menuItem) => {
      console.log('Menu clicked:', menuItem);

      const existingTab = tabs.find((tab) => tab.id === menuItem.name);

      if (existingTab) {
        setActiveTab(existingTab.id);
      } else {
        const Component = componentRegistry[menuItem.component];
        var Icon = iconsRegistry[menuItem.name];

        // Determine if component should be wrapped with error boundary
        const shouldWrap = shouldWrapWithErrorBoundary(Component, menuItem.component);
        const mfeMetadata = getMFEMetadata(menuItem.component);

        // Create tab content with or without error boundary wrapper
        let content;
        if (shouldWrap && Component) {
          // Wrap MFE components with error boundary
          const retryKey = mfeRetryKeys[menuItem.name] || 0;
          content = (
            <MFEComponentWrapper
              key={`${menuItem.name}-${retryKey}`}
              component={Component}
              mfeName={mfeMetadata?.mfeName || menuItem.displayName}
              displayName={menuItem.displayName}
              onError={(error) => {
                console.error(`Error in ${menuItem.displayName}:`, error);
              }}
              onRetry={() => {
                console.log(`Retrying ${menuItem.displayName}`);
                // Increment retry key to force remount and reload the MFE
                setMfeRetryKeys(prev => ({
                  ...prev,
                  [menuItem.name]: (prev[menuItem.name] || 0) + 1
                }));
              }}
            />
          );
        } else if (Component) {
          // Regular components don't need wrapper
          content = <Component />;
        } else {
          // Fallback for missing components
          content = (
            <div style={{ padding: '20px' }}>
              <h2>{menuItem.displayName}</h2>
              <p>Content for {menuItem.displayName}</p>
              <p>This is where the actual component would be rendered.</p>
            </div>
          );
        }

        const newTab = {
          id: menuItem.name,
          title: menuItem.displayName,
          icon: Icon ? <Icon /> : '📄',
          content,
          closable: true
        };

        setTabs(prev => [...prev, newTab]);
        setActiveTab(newTab.id);
      }

      // Navigate if URL is provided (but don't trigger data reload)
      if (menuItem.url) {
        navigate(menuItem.url, { replace: true });
      }
    },
    [tabs, navigate]
  );

  const handleTabChange = useCallback((tabId) => {
    setActiveTab(tabId);
  }, []);

  const handleTabClose = useCallback(
    (tabId) => {
      const tabIndex = tabs.findIndex((tab) => tab.id === tabId);
      const newTabs = tabs.filter((tab) => tab.id !== tabId);

      setTabs(newTabs);

      if (tabId === activeTab && newTabs.length > 0) {
        const newActiveIndex = Math.max(0, tabIndex - 1);
        setActiveTab(newTabs[newActiveIndex].id);
      }
    },
    [tabs, activeTab]
  );

  const handleTabAdd = useCallback(() => {
    const newTab = {
      id: `new-tab-${Date.now()}`,
      title: 'New Tab',
      icon: '➕',
      content: <div style={{ padding: '20px' }}>New tab content</div>,
      closable: true,
    };

    setTabs((prev) => [...prev, newTab]);
    setActiveTab(newTab.id);
  }, []);

  const toggleMenuCollapse = useCallback(() => {
    setIsMenuCollapsed((prev) => !prev);
  }, []);

  // Handle authentication loading
  if (!authState) {
    return (
      <div className="app-container">
        <LoadingScreen message="Initializing..." />
        <ToastContainer />
      </div>
    );
  }

  // Show loading while user data is being fetched (only on initial load)
  if (isLoadingUserData && !userDataLoaded || !localizationReady) {
    return (
      <div className="app-container">
        <LoadingScreen message={isLoadingUserData ? L('Loading') : L('Loading localization')} />
        <ToastContainer />
      </div>
    );
  }

  // Main authenticated application
  console.log('App: Rendering main application');
  return (
    <NotificationProvider isUserSessionActive={activeUserSession}>
      <div className="app-container">
        <Header
          user={activeUser}
          onOpenNotificationsTab={handleOpenNotificationsTab}
        />

        <div className="app-body">
          <LeftMenu
            menuItems={menuItems}
            onMenuClick={handleMenuClick}
            isCollapsed={isMenuCollapsed}
            onToggleCollapse={toggleMenuCollapse}
          />

          <div className="main-content">
            <TabPanel
              tabs={tabs}
              activeTab={activeTab}
              onTabChange={handleTabChange}
              onTabClose={handleTabClose}
              onTabAdd={handleTabAdd}
            />
          </div>
        </div>

        <Footer />

        <React.Suspense
          fallback={<LoadingScreen message="Loading component..." />}
        >
          {/* TODO: do we need this? */}
          <Routes>
            <Route
              path="*"
              element={
                <ProtectedRoute authState={authState}>
                  <div />
                </ProtectedRoute>
              }
            />
          </Routes>
        </React.Suspense>

        <ToastContainer />
      </div>
    </NotificationProvider>
  );
}

export default withAuthGuard(App, {
  customScreenLoader: () => (
    <div className="app-container">
      <LoadingScreen />
    </div>
  ),
});
